import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { FormattedHTMLMessage } from 'react-intl'
import { Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap'
import cx from 'classnames'
import FormattedMessage from '../../components/FormattedMessage/FormattedMessage'
import Loader from '../../components/Loader/Loader'
import {
  loadMyArchive as loadMyArchiveAction,
  loadMyRewards as loadMyRewardsAction,
  myRewards,
} from '../../redux/reducers/myRewards'
import { currentUser } from '../../redux/reducers/user.reducers'
import ClaimedReward from './ClaimedReward'
import ArchivedRewards from './ArchivedRewards'

import './UserRewardHistory.scss'

const UserRewardHistory = ({ rewardsAndArchive, user, loadMyRewards, loadMyArchive }) => {
  const { rewards, archive, rewardsLoaded, archiveLoaded } = rewardsAndArchive

  const [loadingRewards, setLoadingRewards] = useState(!rewardsLoaded)
  const [loadingArchive, setLoadingArchive] = useState(true)
  const [activeTab, setActiveTab] = useState('1')

  useEffect(() => {
    if (rewardsLoaded) {
      setLoadingRewards(false)
      return undefined
    }

    let isMounted = true
    const fetchRewards = async () => {
      const { language } = user.user_detail
      await loadMyRewards(language)
      if (isMounted) {
        setLoadingRewards(false)
      }
    }

    fetchRewards()
    return () => {
      isMounted = false
    }
  }, [loadMyRewards, rewardsLoaded, user])

  useEffect(() => {
    if (activeTab !== '2') {
      return undefined
    }

    if (archiveLoaded) {
      setLoadingArchive(false)
      return undefined
    }

    let isMounted = true
    const fetchArchive = async () => {
      const { language } = user.user_detail
      await loadMyArchive(language)
      if (isMounted) {
        setLoadingArchive(false)
      }
    }

    fetchArchive()
    return () => {
      isMounted = false
    }
  }, [activeTab, archiveLoaded, loadMyArchive, user])

  const toggleTab = tab => {
    if (activeTab !== tab) setActiveTab(tab)
  }

  let activeRewardsContent
  if (loadingRewards) {
    activeRewardsContent = (
      <div className="user-reward-history__loader">
        <Loader noBorder />
      </div>
    )
  } else if (rewards && rewards.length) {
    activeRewardsContent = (
      <>
        {rewards.map((reward, index) => (
          <ClaimedReward
            key={reward.coupon}
            parentKey={index}
            reward={reward}
            email={user.email}
            title={reward.name}
          />
        ))}
      </>
    )
  } else {
    activeRewardsContent = (
      <div className="mt-4">
        <h4>
          <FormattedHTMLMessage id="page.myRewardsPage.title.description" />
        </h4>
        <p>
          <FormattedMessage id="page.myRewardsPage.text.noAccountReward" />
        </p>
      </div>
    )
  }

  let archivedRewardsContent
  if (loadingArchive) {
    archivedRewardsContent = (
      <div className="user-reward-history__loader">
        <Loader noBorder />
      </div>
    )
  } else if (archive && archive.length) {
    archivedRewardsContent = (
      <>
        {archive.map((archiveItem, index) => (
          <ArchivedRewards key={`${archiveItem.couponKey}__${index}`} archive={archiveItem} />
        ))}
      </>
    )
  } else {
    archivedRewardsContent = (
      <div className="mt-4">
        <p>
          <FormattedMessage id="page.myRewardsPage.text.noArchivedRewards" />
        </p>
      </div>
    )
  }

  return (
    <div className="user-reward-history">
      <h4>
        <FormattedHTMLMessage id="page.myRewardsPage.title.description" />
      </h4>
      <p>
        <FormattedHTMLMessage id="page.myRewardsPage.title.note" />
      </p>
      <Nav tabs role="tablist" aria-label="Reward sections">
        <NavItem>
          <NavLink
            className={cx('tab-header', { active: activeTab === '1' })}
            role="tab"
            id="tab-active-rewards"
            aria-selected={activeTab === '1'}
            aria-controls="tabpanel-active-rewards"
            tabIndex={activeTab === '1' ? 0 : -1}
            onClick={() => toggleTab('1')}
          >
            <FormattedHTMLMessage id="page.myRewardsPage.tab.activeRewards" />
            <span className="reward-count">[{rewards.length || 0}]</span>
          </NavLink>
        </NavItem>
        <NavItem>
          <NavLink
            className={cx('tab-header', { active: activeTab === '2' })}
            role="tab"
            id="tab-archived-rewards"
            aria-selected={activeTab === '2'}
            aria-controls="tabpanel-archived-rewards"
            tabIndex={activeTab === '2' ? 0 : -1}
            onClick={() => toggleTab('2')}
          >
            <FormattedHTMLMessage id="page.myRewardsPage.tab.archivedRewards" />
            <span className="reward-count">[{archive.length || 0}]</span>
          </NavLink>
        </NavItem>
      </Nav>
      <TabContent activeTab={activeTab} className="p-0">
        <TabPane
          tabId="1"
          role="tabpanel"
          aria-labelledby="tab-active-rewards"
          id="tabpanel-active-rewards"
          tabIndex={0}
        >
          {activeRewardsContent}
        </TabPane>
        <TabPane
          tabId="2"
          role="tabpanel"
          aria-labelledby="tab-archived-rewards"
          id="tabpanel-archived-rewards"
          tabIndex={0}
        >
          {archivedRewardsContent}
        </TabPane>
      </TabContent>
    </div>
  )
}

UserRewardHistory.propTypes = {
  rewardsAndArchive: PropTypes.oneOfType([PropTypes.array.isRequired, PropTypes.object.isRequired]),
  loadMyRewards: PropTypes.func.isRequired,
  loadMyArchive: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
}

const mapStateToProps = state => ({
  rewardsAndArchive: myRewards(state),
  user: currentUser(state),
})

const mapDispatchToProps = {
  loadMyRewards: loadMyRewardsAction,
  loadMyArchive: loadMyArchiveAction,
}

export default connect(mapStateToProps, mapDispatchToProps)(UserRewardHistory)
